import { useFetchAdminImageList, usePostImagesBulkDelete } from '@api';
import {
  CategorySelectEnum,
  FromPageQueryParamValueEnum,
  optionsConstant,
  PathnameEnum,
  PathParamKeyEnum,
  QueryParamKeyEnum,
  ToastMessageEnum
} from '@constant';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useToast } from './useToast';

//어드민 - 이미지 리스트 페이지 로직에 관여
export const useAdminImageList = () => {
  // 체크박스 상태 관리
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());
  // 삭제 모달 상태 관리
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const { showToast } = useToast();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search); // location.search는 '?key=value' 형태
  const locationPathname = location.pathname;
  const isStandardImageListPage = locationPathname.includes(PathnameEnum.ADMIN_STANDARD_IMAGE_LIST_PAGE);
  const isAdultsOnlyImageListPage = locationPathname.includes(PathnameEnum.ADMIN_ADULTS_ONLY_IMAGE_LIST_PAGE);

  // 이미지 리스트 페이지
  const [searchParams] = useSearchParams();
  const page = searchParams.get(QueryParamKeyEnum.PAGE) ? Number(searchParams.get(QueryParamKeyEnum.PAGE)) : 1;

  const standardTableHeader = [
    { id: 'checkbox', label: '', width: 74 },
    { id: 'id', label: 'ID', width: 73 },
    { id: 'fileName', label: '파일명', width: 226 },
    { id: 'genre', label: '장르', width: 100 },
    { id: 'backgroundCut', label: '배경컷', width: 80 },
    { id: 'characterNumber', label: '인원수', width: 80 },
    { id: 'shot', label: '프레임', width: 80 },
    { id: 'directed', label: '연출', width: 80 },
    { id: 'verticalAngle', label: '세로앵글', width: 80 },
    { id: 'ageRange', label: '연령대', width: 80 },
    { id: 'mainMotionPart', label: '중심 부위', width: 78 },
    { id: 'facialExpression', label: '표정', width: 100 },
    { id: 'deforme', label: '데포르메', width: 78 },
    { id: 'horizontalAngle', label: '가로앵글', width: 80 },
    { id: 'postureCategory', label: '동작구분', width: 100 },
    { id: 'skinship', label: '스킨십', width: 78 },
    { id: 'metaTags', label: '관련 태그', width: 133 }
  ];

  const adultsOnlyTableHeader = [
    { id: 'checkbox', label: '', width: 74 },
    { id: 'id', label: 'ID', width: 74 },
    { id: 'fileName', label: '파일명', width: 336 },
    { id: 'sexualPosition', label: '체위', width: 100 },
    { id: 'femaleBody', label: '여체', width: 100 },
    { id: 'sexualBodyPart', label: '신체 부위', width: 200 },
    { id: 'characterNumber', label: '인원수', width: 120 },
    { id: 'shot', label: '프레임', width: 100 },
    { id: 'horizontalAngle', label: '가로앵글', width: 80 },
    { id: 'verticalAngle', label: '세로앵글', width: 80 },
    { id: 'metaTags', label: '관련 태그', width: 336 }
  ];

  const createParentOptionsQueryParamValue = () => {
    if (isStandardImageListPage) {
      return optionsConstant.parentOptions.STANDARD.id;
    } else if (isAdultsOnlyImageListPage) {
      return optionsConstant.parentOptions.ADULTS_ONLY.id;
    } else {
      return optionsConstant.parentOptions.STANDARD.id;
    }
  };

  const { response, refetch } = useFetchAdminImageList(createParentOptionsQueryParamValue(), page);

  const { mutateAsync, isPending } = usePostImagesBulkDelete();

  // 체크박스 헤더 상태 ('unchecked' | 'partial' | 'checked')
  const getHeaderCheckboxState = () => {
    const totalItems = response?.data?.length || 0;
    const checkedCount = checkedItems.size;

    if (checkedCount === 0) return 'unchecked';
    if (checkedCount > 0 && checkedCount < totalItems) return 'partial';
    if (checkedCount === totalItems) return 'checked';
    return 'unchecked';
  };

  // 헤더 체크박스 클릭 핸들러
  const handleHeaderCheckboxClick = () => {
    const state = getHeaderCheckboxState();

    if (state === 'unchecked' || state === 'partial') {
      // 모두 선택
      const allIds = new Set(response?.data?.map((item) => item.id) || []);
      setCheckedItems(allIds);
    } else if (state === 'checked') {
      // 모두 해제
      setCheckedItems(new Set());
    }
  };

  // 행 체크박스 클릭 핸들러
  const handleRowCheckboxClick = (imageId: number) => {
    setCheckedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const handleImageUploadButtonClick = () => {
    if (isStandardImageListPage) {
      queryParams.set(QueryParamKeyEnum.FROM_PAGE, FromPageQueryParamValueEnum.STANDARD);
      queryParams.set(QueryParamKeyEnum.THEME, CategorySelectEnum.STANDARD);
      const queryString = queryParams.toString();
      navigate(`${PathnameEnum.ADMIN_IMAGE_UPLOAD_PAGE}?${queryString}`);
    } else if (isAdultsOnlyImageListPage) {
      queryParams.set(QueryParamKeyEnum.FROM_PAGE, FromPageQueryParamValueEnum.ADULTS_ONLY);
      queryParams.set(QueryParamKeyEnum.THEME, CategorySelectEnum.ADULTS_ONLY);
      const queryString = queryParams.toString();
      navigate(`${PathnameEnum.ADMIN_IMAGE_UPLOAD_PAGE}?${queryString}`);
    } else {
      queryParams.set(QueryParamKeyEnum.FROM_PAGE, FromPageQueryParamValueEnum.STANDARD);
      queryParams.set(QueryParamKeyEnum.THEME, CategorySelectEnum.STANDARD);
      const queryString = queryParams.toString();
      navigate(`${PathnameEnum.ADMIN_IMAGE_UPLOAD_PAGE}?${queryString}`);
    }
  };

  const tableHeader = () => {
    if (isStandardImageListPage) {
      return standardTableHeader;
    } else if (isAdultsOnlyImageListPage) {
      return adultsOnlyTableHeader;
    } else {
      return [];
    }
  };

  const handleTableRowClick = (imageId: number, sequenceId?: number) => {
    if (sequenceId) {
      queryParams.set(QueryParamKeyEnum.SEQUENCE_ID, String(sequenceId));
    }

    if (isStandardImageListPage) {
      queryParams.set(QueryParamKeyEnum.FROM_PAGE, FromPageQueryParamValueEnum.STANDARD);
      queryParams.set(QueryParamKeyEnum.THEME, CategorySelectEnum.STANDARD);
    } else if (isAdultsOnlyImageListPage) {
      queryParams.set(QueryParamKeyEnum.FROM_PAGE, FromPageQueryParamValueEnum.ADULTS_ONLY);
      queryParams.set(QueryParamKeyEnum.THEME, CategorySelectEnum.ADULTS_ONLY);
    }

    const queryString = queryParams.toString();

    navigate(
      `${PathnameEnum.ADMIN_IMAGE_EDIT_PAGE.replace(PathParamKeyEnum.IMAGE_ID, String(imageId))}?${queryString}`
    );
  };

  const handlePageChange = (value: number) => {
    queryParams.set(QueryParamKeyEnum.PAGE, String(value));
    const queryString = queryParams.toString();
    navigate(`${locationPathname}?${queryString}`, { replace: true });
  };

  const handleDeleteButtonClick = () => {
    setIsDeleteModalVisible(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalVisible(false);
  };

  const handleImageDeleteConfirm = async () => {
    const imageIds = Array.from(checkedItems);

    await mutateAsync({ imageId: imageIds }).then(() => {
      refetch();
      setCheckedItems(new Set());
      handleDeleteModalClose();
      showToast(ToastMessageEnum.IMAGE_DELETE_SUCCESS);
    });
  };

  // 페이지 변경 시 체크박스 상태 초기화
  useEffect(() => {
    setCheckedItems(new Set());
  }, [page, response?.data]);

  return {
    handleImageUploadButtonClick,
    handleTableRowClick,

    standardTableHeader,
    tableHeader,
    response,
    page,
    handlePageChange,

    // 체크박스 관련
    checkedItems,
    getHeaderCheckboxState,
    handleHeaderCheckboxClick,
    handleRowCheckboxClick,

    // 이미지 삭제
    isDeleteModalVisible,
    handleDeleteButtonClick,
    handleDeleteModalClose,
    handleImageDeleteConfirm,
    isPending
  };
};
